import React, { Fragment, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import { usePageSpaceKey } from '@confluence/page-context';

import { i18n } from './attachmentsMacroi18n.ts';
import type { AttachmentsMacroParams } from './attachmentsMacroTypes.ts';

import { useAttachmentsMacroData } from './hooks/useAttachmentsMacroData.ts';
import { AttachmentsTable } from './components/AttachmentsTable.tsx';
import { DownloadAllLink } from './components/DownloadAllLink.tsx';
import { EmptyState } from './components/EmptyState.tsx';
import { AttachmentsMacroUploadLoader } from './components/upload/AttachmentsMacroUploadLoader.tsx';

const EMPTY_PARAMS: AttachmentsMacroParams = {};

export type AttachmentsMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: AttachmentsMacroParams;
};

const AttachmentsMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
}: AttachmentsMacroRendererProps) => {
	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const [spaceKey] = usePageSpaceKey();
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);

	const {
		attachments,
		totalCount,
		currentPage,
		totalPages,
		hasNextPage,
		pageId,
		dspBlocksDownloads,
		// hasAttachFilePermission not used — upload permission is enforced server-side.
		error,
		onPageChange,
		showVersionHistory,
		showUpload,
		showPreview,
		refetch,
	} = useAttachmentsMacroData(macroParams ?? EMPTY_PARAMS, contentId, spaceKey ?? undefined);

	const handleAttachmentUpdated = useCallback(() => {
		refetch?.();
	}, [refetch]);

	const handlePageClick = useCallback((p: number) => () => onPageChange(p), [onPageChange]);

	if (error) {
		return (
			<Fragment>
				<Box padding="space.300" testId="attachments-error">
					<Stack alignInline="center">
						<Text>
							<FormattedMessage {...i18n.errorGeneric} />
						</Text>
					</Stack>
				</Box>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={error}
				/>
			</Fragment>
		);
	}

	const isEmpty = attachments.length === 0;

	const showEmptyState = isEmpty;

	const showDownloadAll = totalCount > 1 && !dspBlocksDownloads;
	// Show the upload area when the macro param enables it. Permission is
	// enforced server-side on the upload POST (403 → error state).
	const showUploadArea = showUpload && Boolean(contentId);

	const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
	const showPagination = totalPages > 1;

	return (
		<Fragment>
			<div data-testid="attachments-macro">
				<Stack space="space.025">
					{showEmptyState ? (
						<EmptyState />
					) : (
						<Fragment>
							<AttachmentsTable
								attachments={attachments}
								dspBlocksDownloads={dspBlocksDownloads}
								showVersionHistory={showVersionHistory}
								showPreview={showPreview}
								contentId={pageId || contentId || undefined}
								spaceKey={spaceKey ?? undefined}
								onAttachmentUpdated={handleAttachmentUpdated}
							/>

							{showPagination && (
								<Inline space="space.050" alignBlock="center" testId="attachments-pagination">
									<Button
										appearance="subtle"
										spacing="compact"
										isDisabled={currentPage <= 1}
										onClick={() => onPageChange(currentPage - 1)}
										testId="attachments-pagination-prev"
									>
										<FormattedMessage {...i18n.paginationPrev} />
									</Button>
									{pages.map((p) => (
										<Button
											key={p}
											appearance="subtle"
											spacing="compact"
											isSelected={p === currentPage}
											onClick={handlePageClick(p)}
											testId={`attachments-pagination-page-${p}`}
										>
											{p}
										</Button>
									))}
									<Button
										appearance="subtle"
										spacing="compact"
										isDisabled={currentPage >= totalPages && !hasNextPage}
										onClick={() => onPageChange(currentPage + 1)}
										testId="attachments-pagination-next"
									>
										<FormattedMessage {...i18n.paginationNext} />
									</Button>
								</Inline>
							)}
						</Fragment>
					)}

					{showUploadArea && contentId && (
						<AttachmentsMacroUploadLoader
							contentId={contentId}
							onFilesUploaded={handleAttachmentUpdated}
						/>
					)}

					{showDownloadAll && <DownloadAllLink pageId={pageId} />}
				</Stack>
			</div>
			<MacroExperienceSuccess
				name={experienceName}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
			/>
		</Fragment>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.errorGeneric} />;

export const AttachmentsMacroRenderer = (
	props: AttachmentsMacroRendererProps,
): React.JSX.Element => (
	<GenericErrorBoundary
		renderOnError={renderErrorFallback}
		attribution={Attribution.PAGE_EXTENSIONS}
	>
		<AttachmentsMacroRendererComponent {...props} />
	</GenericErrorBoundary>
);
